import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Modal, ModalContent, ModalFooter, ModalProps, ModalTitle, cn } from '@dashboard/ui'
import { usePydenticForm } from '@/libs'
import { FieldGroup, ToastSeverity, useToast } from '@/ui'
import { Theme } from 'src/layouts/token-studio'
import { Controller } from 'react-hook-form'
import Editor from 'react-simple-code-editor'
import { highlight, languages } from 'prismjs'
import { useMemo } from 'react'
import 'prismjs/components/prism-json'
import 'prismjs/themes/prism.css'

interface ThemeRawModalProps extends ModalProps {
  segmentedThemeQueryKey: (string | undefined)[]
  theme: Theme | undefined
}

export const ThemeRawModal = ({ segmentedThemeQueryKey, theme, ...props }: ThemeRawModalProps) => {
  const ql = useQueryClient()
  const { t } = useTranslation('hub-theme')
  const showToast = useToast()

  const {
    formState: { isSubmitting, isDirty },
    watch,
    setValue,
    handleSubmit,
    control,
  } = usePydenticForm({
    values: {
      theme_raw: theme ? JSON.stringify(theme, null, 2) : '',
    },
  })

  const json = watch('theme_raw')

  const isValid = useMemo(() => {
    try {
      JSON.parse(json)
    } catch (error) {
      return false
    }
    return true
  }, [json])

  const onClickPasteFromClipboard = () => {
    navigator.clipboard.readText().then(text => {
      setValue('theme_raw', text)
    })
  }

  const onSubmit = handleSubmit(async data => {
    try {
      const editedTheme = JSON.parse(data.theme_raw)
      ql.setQueryData<Theme>(segmentedThemeQueryKey, () => editedTheme)
      showToast({ message: t('raw-modal.success'), severity: ToastSeverity.success })
      props.onClose()
    } catch (error) {
      console.error(error)
      showToast({ message: t('raw-modal.error'), severity: ToastSeverity.error })
    }
  })

  return (
    <Modal {...props} size="lg">
      <form onSubmit={onSubmit}>
        <ModalContent>
          <ModalTitle subtitle={t('raw-modal.subtitle')} onClose={props.onClose}>
            {t('raw-modal.title')}
          </ModalTitle>
          <FieldGroup>
            <Controller
              control={control}
              name="theme_raw"
              render={({ field }) => (
                <Editor
                  className={cn(
                    'rounded-md border border-border-primary transition-colors',
                    !isValid && 'border-border-error bg-fg-error-tertiary',
                  )}
                  value={field.value}
                  onValueChange={field.onChange}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && e.metaKey) {
                      e.preventDefault()
                      e.stopPropagation()
                      onSubmit()
                    }
                  }}
                  highlight={code => highlight(code, languages.json, 'json')}
                  padding={10}
                  style={{
                    fontFamily: '"Fira code", "Fira Mono", monospace',
                    lineHeight: 1.25,
                    fontSize: 14,
                  }}
                />
              )}
            />
          </FieldGroup>
          <FieldGroup>
            <Button variant="outline" color="secondary" size="md" onClick={onClickPasteFromClipboard}>
              Paste from clipboard
            </Button>
          </FieldGroup>
        </ModalContent>
        <ModalFooter>
          <Button variant="outline" color="secondary" size="md" onClick={props.onClose}>
            {t('raw-modal.cancel')}
          </Button>
          <Button loading={isSubmitting} disabled={!isDirty || !isValid} type="submit" size="md">
            {t('raw-modal.update')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}
